import { FACILITATOR_CONTRACT_FIELD_LIMITS } from '../types/FacilitatorContract';

export const FACILITATOR_CONTRACT_ID_PREFIX = 'fcontract_';
export const FACILITATOR_CONTRACT_ID_PATTERN = /^fcontract_[a-f0-9]{32}$/;
export const FACILITATOR_CONTRACT_PATH_PREFIX = '/facilitator-contract';

export const FACILITATOR_CONTRACT_TEMPLATE_ID_PREFIX = 'fctmpl_';
export const FACILITATOR_CONTRACT_TEMPLATE_ID_PATTERN = /^fctmpl_[a-f0-9]{32}$/;

export const AWE_WANDER_FACILITATOR_TEMPLATE_ID = 'awe-wander-facilitator-agreement';

function generatePrefixedHexId(prefix: string): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${prefix}${hex}`;
}

export function generateFacilitatorContractId(): string {
  return generatePrefixedHexId(FACILITATOR_CONTRACT_ID_PREFIX);
}

export function isFacilitatorContractId(id: string): boolean {
  return FACILITATOR_CONTRACT_ID_PATTERN.test(id);
}

export function generateFacilitatorContractTemplateId(): string {
  return generatePrefixedHexId(FACILITATOR_CONTRACT_TEMPLATE_ID_PREFIX);
}

export function isFacilitatorContractTemplateId(id: string): boolean {
  return FACILITATOR_CONTRACT_TEMPLATE_ID_PATTERN.test(id);
}

export function getFacilitatorContractPath(id: string): string {
  return `${FACILITATOR_CONTRACT_PATH_PREFIX}/${encodeURIComponent(id)}`;
}

export function getFacilitatorContractUrl(
  id: string,
  origin = typeof window === 'undefined' ? '' : window.location.origin,
): string {
  return `${origin}${getFacilitatorContractPath(id)}`;
}

export function isFacilitatorContractPublicPath(pathname: string): boolean {
  const path =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return path === FACILITATOR_CONTRACT_PATH_PREFIX || path.startsWith(`${FACILITATOR_CONTRACT_PATH_PREFIX}/`);
}

export type ExtraContractRecipient = {
  name: string;
  email: string;
};

export function parseExtraContractRecipients(text: string): ExtraContractRecipient[] {
  const recipients: ExtraContractRecipient[] = [];
  const seen = new Set<string>();

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }

    const angled = line.match(/^(.*?)\s*<([^>]+)>$/);
    const name = angled ? angled[1].trim() : '';
    const email = (angled ? angled[2] : line).trim().toLowerCase();
    if (!email || seen.has(email)) {
      continue;
    }
    seen.add(email);
    recipients.push({
      name: name || email.split('@')[0],
      email,
    });
  }

  return recipients;
}

export type FacilitatorContractInviteEmailInput = {
  recipientName: string;
  contractTitle: string;
  signLink: string;
  personalNote?: string;
};

export function clipFacilitatorContractPersonalNote(value: string): string {
  return value.trim().slice(0, FACILITATOR_CONTRACT_FIELD_LIMITS.personalNote);
}

export function buildFacilitatorContractInviteText({
  recipientName,
  contractTitle,
  signLink,
  personalNote,
}: FacilitatorContractInviteEmailInput): string {
  const note = clipFacilitatorContractPersonalNote(personalNote || '');
  const noteBlock = note ? `${note}\n\n` : '';
  return `
Dear ${recipientName},

${noteBlock}Overhear has sent you an agreement to read and sign.

${contractTitle}

Please open the link below, read the agreement, and complete the signature form:

${signLink}

If you have any questions, email hello@theoverhear.app.

Best regards,
The Overhear Workshop Team
  `.trim();
}

export type FacilitatorProjectLink = {
  projectId: string;
  projectName: string;
};

export function clipFacilitatorProjectLink(
  projectId?: string,
  projectName?: string,
): FacilitatorProjectLink {
  return {
    projectId: (projectId || '').trim().slice(0, FACILITATOR_CONTRACT_FIELD_LIMITS.projectId),
    projectName: (projectName || '').trim().slice(0, FACILITATOR_CONTRACT_FIELD_LIMITS.projectName),
  };
}

export function hasFacilitatorProjectLink(link: { projectId?: string } | null | undefined): boolean {
  return Boolean(link?.projectId?.trim());
}

